import { Box } from "@mui/material";

import { VariationTabs } from "@/features/BroadcastV2/components/VariationTabs";
import {
  useBroadcastComposeActions,
  useBroadcastComposeStore,
  useBroadcastComposeStoreShallow,
} from "@/features/BroadcastV2/store/broadcastComposeStore";
import {
  smsVariationFrameName,
  variationLabel,
} from "@/features/BroadcastV2/store/variationTypes";

/**
 * Toggle-only variation tab strip rendered above the right (preview) column of
 * the broadcast compose pages. It reads the shared compose store directly, so it
 * stays in sync with the left-column tabs — selecting a variant here updates the
 * same `activeVariationIndex` the left tabs use. Add/remove affordances live only
 * on the left tabs, so this strip omits them. Renders nothing unless A/B is on.
 */
export function RightColumnVariationTabs({
  confirmLeaveTile,
}: {
  /** Asks before switching away from the active tile; resolves true to go. */
  confirmLeaveTile?: () => Promise<boolean>;
} = {}) {
  const variations = useBroadcastComposeStoreShallow((s) => s.variations);
  const activeVariationIndex = useBroadcastComposeStore(
    (s) => s.activeVariationIndex,
  );
  const name = useBroadcastComposeStore((s) => s.name);
  const actions = useBroadcastComposeActions();

  if (variations.length <= 1) return null;

  return (
    // Small top margin so the strip doesn't butt against the heading above it.
    // Only rendered when A/B is on, so the spacing is inherently conditional.
    <Box sx={{ mt: 2 }}>
      <VariationTabs
        items={variations.map((v, i) => ({
          id: v.id,
          label:
            v.email?.selectedFrame?.name ||
            (v.email == null
              ? smsVariationFrameName(name, i, variations.length)
              : v.name || variationLabel(i)),
          removable: false,
        }))}
        activeIndex={activeVariationIndex}
        onSelect={(index) => {
          if (index === activeVariationIndex) return;
          if (!confirmLeaveTile) return actions.setActiveVariation(index);
          void confirmLeaveTile().then((leave) => {
            if (leave) actions.setActiveVariation(index);
          });
        }}
      />
    </Box>
  );
}
